import type { PatientPackageResponse } from '@/api/generated/model';

/**
 * Which of a patient's packages can pay for a session of this service — the
 * options of the package card on «ثبت جلسه» and «نهایی کردن».
 *
 * The same four questions the server asks in `consumePackage`, answered from
 * the sale as the API already returns it:
 *
 *  · the sale is usable today (`isConsumable`: not cancelled, refunded,
 *    expired or used up — the server's own `consumeBlocker`);
 *  · it has a session left overall;
 *  · its frozen snapshot covers this service (`procedures[].procedureId` is
 *    the CATALOGUE id, the one a session carries);
 *  · that service's own cap, if the bundle has one, is not reached
 *    (`remaining`, already reduced by the pool).
 *
 * Asking here does not replace the server's check — a sale used up at the
 * other desk a second ago is still refused there, in Persian. It keeps the card
 * from offering a package the server is certain to refuse.
 *
 * Earliest expiry first, then the oldest sale: the one to use up before it is
 * lost, which is also the one the desk would pick.
 */
export function eligiblePackages(
  sales: readonly PatientPackageResponse[] | undefined,
  procedureId: string | null,
): PatientPackageResponse[] {
  if (!sales || !procedureId) return [];
  return sales
    .filter((sale) => sessionsLeftFor(sale, procedureId) > 0)
    .sort((a, b) => {
      const ea = a.expiresAt ? Date.parse(a.expiresAt) : Number.POSITIVE_INFINITY;
      const eb = b.expiresAt ? Date.parse(b.expiresAt) : Number.POSITIVE_INFINITY;
      if (ea !== eb) return ea - eb;
      return Date.parse(a.purchasedAt) - Date.parse(b.purchasedAt);
    });
}

/**
 * Sessions of THIS service still available on a sale: the smaller of the
 * pool and the service's own cap. `0` when the sale cannot be drawn at all.
 */
export function sessionsLeftFor(sale: PatientPackageResponse, procedureId: string): number {
  if (!sale.isConsumable || sale.sessionsRemaining <= 0) return 0;
  const line = sale.procedures.find((item) => item.procedureId === procedureId);
  if (!line) return 0;
  const cap = line.remaining ?? Number.POSITIVE_INFINITY;
  return Math.max(0, Math.min(sale.sessionsRemaining, cap));
}
